import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_Hex = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">🎨 CSS HEX Colors</h1>

      <p className="tutorial-intro">
        HEX colors in CSS are written with a <code>#</code> followed by{" "}
        <strong>3</strong>, <strong>6</strong>, or <strong>8</strong>{" "}
        hexadecimal digits. Each pair represents the intensity of{" "}
        <strong>red</strong>, <strong>green</strong>, and <strong>blue</strong>.
        Optionally, the last pair represents <strong>alpha</strong> (opacity).
      </p>

      {/* 6-digit HEX */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ 6-digit HEX (#RRGGBB)</h2>
        <p>
          Each color is defined by two digits for red, green, and blue. Values
          range from <code>00</code> (0) to <code>FF</code> (255).
        </p>
        <CodeCopyBlock
          code={`h1 { color: #ff0000; }   /* Red */
p  { color: #00ff00; }   /* Green */
div { color: #0000ff; }  /* Blue */`}
        />

        <div style={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
          <div
            style={{
              backgroundColor: "#ff0000",
              color: "white",
              padding: "1rem",
              borderRadius: "8px",
              flex: 1,
              textAlign: "center",
            }}
          >
            #ff0000 → Red
          </div>
          <div
            style={{
              backgroundColor: "#00ff00",
              color: "black",
              padding: "1rem",
              borderRadius: "8px",
              flex: 1,
              textAlign: "center",
            }}
          >
            #00ff00 → Green
          </div>
          <div
            style={{
              backgroundColor: "#0000ff",
              color: "white",
              padding: "1rem",
              borderRadius: "8px",
              flex: 1,
              textAlign: "center",
            }}
          >
            #0000ff → Blue
          </div>
        </div>
      </div>

      {/* 3-digit HEX */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ 3-digit HEX (#RGB)</h2>
        <p>
          A shorthand form: each digit is doubled automatically. For example,
          <code>#f00</code> = <code>#ff0000</code>.
        </p>
        <CodeCopyBlock
          code={`h1 { color: #f00; }   /* Red */
p  { color: #0f0; }   /* Green */
div { color: #00f; }  /* Blue */`}
        />

        <div style={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
          <div
            style={{
              backgroundColor: "#f00",
              color: "white",
              padding: "1rem",
              borderRadius: "8px",
              flex: 1,
              textAlign: "center",
            }}
          >
            #f00 → Red
          </div>
          <div
            style={{
              backgroundColor: "#0f0",
              color: "black",
              padding: "1rem",
              borderRadius: "8px",
              flex: 1,
              textAlign: "center",
            }}
          >
            #0f0 → Green
          </div>
          <div
            style={{
              backgroundColor: "#00f",
              color: "white",
              padding: "1rem",
              borderRadius: "8px",
              flex: 1,
              textAlign: "center",
            }}
          >
            #00f → Blue
          </div>
        </div>
      </div>

      {/* 8-digit HEX */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ 8-digit HEX (#RRGGBBAA)</h2>
        <p>
          The last two digits define <strong>alpha transparency</strong>.{" "}
          <code>00</code> = fully transparent, <code>FF</code> = fully opaque.
        </p>
        <CodeCopyBlock
          code={`div { background-color: #ff000080; } /* Semi-transparent red */`}
        />

        <div
          style={{
            position: "relative",
            background: "#eee",
            padding: "2rem",
            borderRadius: "8px",
            marginTop: "1rem",
          }}
        >
          <div
            style={{
              backgroundColor: "#ff000080",
              padding: "1rem",
              borderRadius: "6px",
              color: "black",
              fontWeight: "bold",
            }}
          >
            #ff000080 → Semi-transparent Red
          </div>
          <p style={{ marginTop: "1rem", color: "#444" }}>
            You can see the gray background through this box.
          </p>
        </div>
      </div>

      {/* Color Mixing */}
      <div className="tutorial-section">
        <h2 className="section-title">4️⃣ HEX Shades & Mixing</h2>
        <p>
          You can mix values to create new shades. For example:{" "}
          <code>#ffa500</code> = orange, <code>#800080</code> = purple,{" "}
          <code>#008080</code> = teal.
        </p>
        <CodeCopyBlock
          code={`div { background-color: #ffa500; }  /* Orange */
div { background-color: #800080; }  /* Purple */
div { background-color: #008080; }  /* Teal */`}
        />

        <div
          style={{
            display: "flex",
            gap: "1rem",
            marginTop: "1rem",
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              backgroundColor: "#ffa500",
              padding: "1rem",
              borderRadius: "8px",
              flex: 1,
              textAlign: "center",
              color: "black",
            }}
          >
            #ffa500 → Orange
          </div>
          <div
            style={{
              backgroundColor: "#800080",
              padding: "1rem",
              borderRadius: "8px",
              flex: 1,
              textAlign: "center",
              color: "white",
            }}
          >
            #800080 → Purple
          </div>
          <div
            style={{
              backgroundColor: "#008080",
              padding: "1rem",
              borderRadius: "8px",
              flex: 1,
              textAlign: "center",
              color: "white",
            }}
          >
            #008080 → Teal
          </div>
        </div>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          HEX codes are compact and widely used in web design. Next, explore{" "}
          <a href="/css/hsl" className="learn-more">
            CSS HSL Colors
          </a>{" "}
          to learn another way of describing colors.
        </p>
      </div>
    </div>
  );
};

export default CSS_Hex;
